import type { Position } from "../types/grid.js";
import type { GridMap } from "../grid/GridMap.js";
import { canMoveTo } from "../grid/CollisionDetector.js";
import { randomInt } from "../combat/dice.js";

/**
 * Gravity-point patrol behaviour.
 *
 * Each enemy has an `anchor` cell (authored in Tiled) and a `leashRadius`.
 * While IDLE / PATROLLING, they random-walk within `leashRadius` Chebyshev
 * distance of their anchor. `leashRadius: 0` enemies (bosses, posted guards)
 * never move.
 *
 * The core decision is a pure function: given the current state, the grid,
 * an occupancy predicate, and a seeded RNG, it returns either the next
 * step or null (stay put this tick). This makes the behaviour fully
 * testable without spinning up a CombatManager.
 */

const PATROL_STEP_CARDINAL: readonly Position[] = [
  { x: 0, z: -1 }, // north
  { x: 1, z: 0 },  // east
  { x: 0, z: 1 },  // south
  { x: -1, z: 0 }, // west
];

// `chebyshev` lives in `../grid/distance.ts`; imported locally and re-exported
// so existing consumers (and the `@engine/ai/patrol.js` test import) keep
// working without forcing every call site to move in the same commit.
import { chebyshev } from "../grid/distance.js";
export { chebyshev };

export interface PatrolContext {
  readonly current: Position;
  readonly anchor: Position;
  readonly leashRadius: number;
  readonly grid: GridMap;
  /** Returns true if `pos` is currently free (no other enemy there). */
  readonly isCellFree: (pos: Position) => boolean;
}

/**
 * Pick the next patrol step — a single orthogonal neighbour that keeps the
 * enemy within leash, is passable, and is unoccupied. Returns null when
 * the leash is 0, when no neighbour qualifies, or when the enemy rolls
 * "stay put" (a fifth option is included so patrols aren't constantly in
 * motion).
 *
 * `randRange` is injected so tests can hand in a deterministic source; the
 * engine default uses `combat/dice.randomInt`, which is seeded at boot.
 */
export function chooseNextPatrolStep(
  ctx: PatrolContext,
  randRange: (min: number, max: number) => number = randomInt,
): Position | null {
  if (ctx.leashRadius <= 0) return null;

  const candidates: Position[] = [];
  for (const step of PATROL_STEP_CARDINAL) {
    const next: Position = { x: ctx.current.x + step.x, z: ctx.current.z + step.z };
    if (chebyshev(next, ctx.anchor) > ctx.leashRadius) continue;
    if (!canMoveTo(ctx.grid, next)) continue;
    if (!ctx.isCellFree(next)) continue;
    candidates.push(next);
  }

  if (candidates.length === 0) return null;

  // Include a "stay put" option so wandering feels intermittent rather than
  // frenetic. Index `candidates.length` represents stay; any other index
  // picks a cardinal step.
  const pick = randRange(0, candidates.length);
  if (pick === candidates.length) return null;
  return candidates[pick] ?? null;
}

/**
 * Clamp a proposed position back inside the leash. Used when chase-mode
 * finishes and an enemy needs to return toward its anchor — callers can
 * feed the anchor as the target and let pathing shrink the distance.
 */
export function isWithinLeash(pos: Position, anchor: Position, leashRadius: number): boolean {
  return chebyshev(pos, anchor) <= leashRadius;
}
